import React from "react";
import type { ParentSummary } from "src/generated/hooks/form-service/requests";

export type GuardianChildRef = {
  id: string;
  firstName: string;
  lastName: string;
};

// A guardian already in the system, selected from search. Linked by its real id;
// never recreated.
export type ExistingGuardianEntry = {
  kind: "existing";
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string | null;
  phoneNumber: string | null;
  children: GuardianChildRef[];
};

// A guardian being created with the student. Carries a client-generated id so the
// relationship can reference it in the same createFamily call.
export type NewGuardianEntry = {
  kind: "new";
  id: string;
  // Display-only: once confirmed via "Add parent/guardian", the editable sub-form
  // collapses into a read-only chip. The entry is in the payload either way.
  confirmed: boolean;
  firstName: string;
  middleName: string | null;
  lastName: string;
  emailAddress: string | null;
  phoneNumber: string | null;
  referenceId: string | null;
  useStudentAddress: boolean;
  streetAddress: string | null;
  streetAddressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
};

export type GuardianEntry = ExistingGuardianEntry | NewGuardianEntry;

export function useGuardianEntries() {
  const [entries, setEntries] = React.useState<GuardianEntry[]>([]);

  const addExisting = React.useCallback((parent: ParentSummary) => {
    setEntries((prev) => {
      // Ignore a guardian already in the list.
      if (prev.some((entry) => entry.id === parent.id)) return prev;
      return [
        ...prev,
        {
          kind: "existing",
          id: parent.id,
          firstName: parent.first_name,
          lastName: parent.last_name,
          emailAddress: parent.email_address,
          phoneNumber: parent.phone_number,
          children: parent.children.map((child) => ({
            id: child.id,
            firstName: child.first_name,
            lastName: child.last_name,
          })),
        },
      ];
    });
  }, []);

  // Add an empty, editable new guardian to the payload, prefilled from the search
  // text. Its fields are edited in place via updateDraft; nothing is created until
  // the page's Create button submits the whole family.
  const addDraft = React.useCallback(
    (prefill: {
      emailAddress?: string | null;
      phoneNumber?: string | null;
    }) => {
      setEntries((prev) => [
        ...prev,
        {
          kind: "new",
          id: crypto.randomUUID(),
          confirmed: false,
          firstName: "",
          middleName: null,
          lastName: "",
          emailAddress: prefill.emailAddress ?? null,
          phoneNumber: prefill.phoneNumber ?? null,
          referenceId: null,
          useStudentAddress: true,
          streetAddress: null,
          streetAddressLine2: null,
          city: null,
          state: null,
          zipCode: null,
        },
      ]);
    },
    []
  );

  const updateDraft = React.useCallback(
    (id: string, patch: Partial<NewGuardianEntry>) => {
      setEntries((prev) =>
        prev.map((entry) =>
          entry.id === id && entry.kind === "new"
            ? { ...entry, ...patch }
            : entry
        )
      );
    },
    []
  );

  // Collapse a new draft into a read-only chip. The entry stays in the payload.
  const confirmDraft = React.useCallback((id: string) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id && entry.kind === "new"
          ? { ...entry, confirmed: true }
          : entry
      )
    );
  }, []);

  const remove = React.useCallback((id: string) => {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  return { entries, addExisting, addDraft, updateDraft, confirmDraft, remove };
}
